/**
 * Label printing as one DYMO job. Framework free so it can be unit tested and
 * reused by any surface that can hand it an ordered list of items.
 *
 * Why one job: DYMO drops a job that arrives while the printer is still busy
 * with the previous one, and still reports it as sent (2026-10-13 live test,
 * ENG-2071). DYMO Connect on Mac has no job status to wait on, so per label
 * sending would need a blind wait. A label set (the template plus one record
 * per item) prints every label from a single request.
 *
 * How a record is built: in a label set, each record replaces a whole object's
 * content (a text box, a barcode, a QR code) by object name. So for every
 * object whose content holds a placeholder, we take its text from the template,
 * fill in the item's values with `customizeLabel`, and send that as the
 * record's value. Objects with no placeholder (a logo, a fixed heading) stay as
 * the template draws them. A text box that mixes styles is expected to print
 * in the object's base style; that's still to be checked on hardware.
 */

import { customizeLabel } from "@/utils/labels/labelUtils";
import type { OrgInfo } from "@/utils/labels/labelUtils";
import {
  DymoBatchError,
  batchTimeoutMs,
  buildPrintParamsXml,
  printLabelSet,
} from "@/utils/labels/dymo";
import type { BatchResult, PrinterType } from "@/utils/labels/dymo";

/** Labels (items × copies) above which the dialog warns. A warning, never a cap. */
export const BULK_PRINT_SOFT_CAP = 200;

// `#token` or `{{ token }}`, but not an XML character reference like `&#x0D;`.
// A character class instead of a lookbehind, which older Safari can't parse.
const PLACEHOLDER = /(?:^|[^&])#[a-z]|\{\{/i;
// Joins every object's text into one string so `customizeLabel` runs once per
// item, not once per object.
const SEPARATOR = "␞";

export type RecordField = { name: string; text: string };

const innerOf = (xml: string, tag: string) =>
  Array.from(
    xml.matchAll(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, "g")),
    (m) => m[1]
  );

/**
 * The objects a record needs to fill, with their template text still XML
 * escaped (so `customizeLabel`'s escaping of values keeps the result valid).
 * Text and address box lines join with newlines, and a barcode split across several
 * `DataString`s joins into one. Image objects are never record fields.
 */
export function extractRecordFields(labelXml: string): RecordField[] {
  const fields: RecordField[] = [];
  const objects = Array.from(
    labelXml.matchAll(
      /<(TextObject|AddressObject|BarcodeObject|QRCodeObject)>\s*<Name>([^<]*)<\/Name>([\s\S]*?)<\/\1>/g
    )
  );
  objects.forEach(([, type, name, body]) => {
    let text: string;
    if (type === "TextObject" || type === "AddressObject") {
      const lines = innerOf(body, "LineTextSpan");
      text = lines.length
        ? lines.map((line) => innerOf(line, "Text").join("")).join("\n")
        : innerOf(body, "String").join("") || innerOf(body, "Text").join("");
    } else {
      text =
        innerOf(body, "DataString").join("") || innerOf(body, "Text").join("");
    }
    if (PLACEHOLDER.test(text)) fields.push({ name, text });
  });
  return fields;
}

// Object names come from the template already escaped for element content;
// a quote still needs escaping inside the attribute.
const attr = (value: string) => value.replace(/"/g, "&quot;");

/** The label set XML for `items`, one record per item, in order. */
export async function buildLabelSetXml<T>({
  labelXml,
  items,
  buildItemInfo,
  orgInfo,
}: {
  labelXml: string;
  items: T[];
  buildItemInfo: (item: T) => any | Promise<any>;
  /** Fetched once per run by the caller, so a run makes one `/orginfo` request. */
  orgInfo: OrgInfo;
}): Promise<string> {
  const fields = extractRecordFields(labelXml);
  const joined = fields.map((f) => f.text).join(SEPARATOR);
  const records = await Promise.all(
    items.map(async (item) => {
      if (fields.length === 0) return "<LabelRecord></LabelRecord>";
      const filled = (
        await customizeLabel(joined, await buildItemInfo(item), { orgInfo })
      ).split(SEPARATOR);
      if (filled.length !== fields.length) {
        throw new Error("An item's values couldn't be matched to the label");
      }
      const data = fields
        .map(
          (f, i) =>
            `<ObjectData Name="${attr(f.name)}">${filled[i]}</ObjectData>`
        )
        .join("");
      return `<LabelRecord>${data}</LabelRecord>`;
    })
  );
  return `<LabelSet>${records.join("")}</LabelSet>`;
}

export type BatchOutcome = {
  result: BatchResult;
  /** Set when result isn't "accepted". */
  reason?: string;
};

export type SendBatch = (args: {
  labelXml: string;
  labelSetXml: string;
  paramsXml: string;
  itemCount: number;
}) => Promise<void>;

/**
 * Builds the label set and hands it to DYMO Connect as one job. Never throws:
 * the outcome says whether DYMO took it, said no, or we stopped waiting. A
 * throw while building (a failed lookup, say) comes back as rejected, since
 * nothing was sent.
 */
export async function printLabelsAsBatch<T>({
  items,
  labelXml,
  printerName,
  printerType,
  copies,
  buildItemInfo,
  orgInfo,
  send,
}: {
  items: T[];
  labelXml: string;
  printerName: string;
  printerType: PrinterType;
  copies: number;
  buildItemInfo: (item: T) => any | Promise<any>;
  orgInfo: OrgInfo;
  /** Injectable sender; defaults to the real DYMO hand off. */
  send?: SendBatch;
}): Promise<BatchOutcome> {
  try {
    if (items.length === 0) {
      return { result: "rejected", reason: "There are no items to print" };
    }
    const labelSetXml = await buildLabelSetXml({
      labelXml,
      items,
      buildItemInfo,
      orgInfo,
    });
    const paramsXml = buildPrintParamsXml({ copies, printerType });
    const sendBatch: SendBatch =
      send ||
      ((args) =>
        printLabelSet({
          labelXml: args.labelXml,
          labelSetXml: args.labelSetXml,
          paramsXml: args.paramsXml,
          printerName,
          timeoutMs: batchTimeoutMs(args.itemCount),
        }));
    await sendBatch({
      labelXml,
      labelSetXml,
      paramsXml,
      itemCount: items.length,
    });
    return { result: "accepted" };
  } catch (reason) {
    if (reason instanceof DymoBatchError) {
      return { result: reason.result, reason: reason.message };
    }
    return {
      result: "rejected",
      reason: reason instanceof Error ? reason.message : String(reason),
    };
  }
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Snackbar copy for an accepted batch. */
export const describeAccepted = (labelCount: number, printerName: string) =>
  `Sent ${labelCount} ${plural(
    labelCount,
    "label",
    "labels"
  )} to ${printerName}.`;

/** Dialog copy for a rejected batch. Nothing printed, so trying again is safe. */
export const describeRejected = (reason?: string) =>
  `DYMO Connect didn't take the batch. ${
    reason ? `${reason.replace(/[.\s]+$/, "")}. ` : ""
  }Nothing was sent to the printer, so it's safe to try again.`;

/** Dialog copy when DYMO Connect didn't answer in time. */
export const NO_ANSWER_MESSAGE =
  "We didn't hear back from DYMO Connect. The labels may still be printing. Check the printer before you print again, or you could end up with duplicates.";
